import { ClearRounded } from '@mui/icons-material';
import {
  alpha,
  Box,
  IconButton,
  InputAdornment,
  SxProps,
  TextField,
  TextFieldProps,
  Theme,
} from '@mui/material';

import { SearchIcon } from '../../icons';

const customStyles: Record<string, SxProps<Theme>> = {
  root: {
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
    '& .MuiInputBase-root': {
      borderRadius: '8px',
      paddingRight: '0px',
      paddingLeft: '0px',
      '&.MuiInputBase-multiline': {
        '& .MuiInputBase-input': {
          padding: '6px 12px',
          height: '32px',
          borderRadius: '8px',
        },
      },
      '& .MuiOutlinedInput-notchedOutline': {
        border: (theme: Theme) => `1px solid ${theme.palette.secondary.light}`,
      },
      '&.Mui-focused': {
        '& .MuiOutlinedInput-notchedOutline': {
          border: (theme: Theme) => `1px solid ${theme.palette.primary.main}`,
          boxShadow: (theme: Theme) => `0 0 0 2px ${alpha(theme.palette.primary.main, 0.1)}`,
          outline: 'none',
        },
      },
      '&:hover': {
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: (theme: Theme) => theme.palette.primary.main,
        },
      },
      '& .MuiInputBase-input': {
        padding: '0px',
        height: '32px',
      },
      '&.Mui-disabled': {
        backgroundColor: '#F3F5F9',
      },
    },
  },
  // Inline mode: search icon hugs the left edge with a small left margin.
  searchIconStart: {
    width: '12px',
    height: '12px',
    margin: '0 0 0 14px',
  },
  // Trailing variant: the icon is purely decorative — filtering happens on
  // every keystroke via the consumer's onChange. Sits behind a vertical
  // divider on the right and renders in the primary color.
  searchIconEnd: {
    width: '16px',
    height: '16px',
    color: (theme: Theme) => theme.palette.primary.main,
  },
  searchEndAdornmentWrap: {
    display: 'flex',
    alignItems: 'center',
    paddingRight: '12px',
  },
  searchEndDivider: {
    width: '1px',
    height: '24px',
    margin: '0 12px 0 0',
    backgroundColor: (theme: Theme) => theme.palette.secondary.light,
    flexShrink: 0,
  },
  // Trailing-variant input gets explicit horizontal padding so the
  // placeholder/value never touch the rounded edges. The deeper selector
  // wins over the root's `padding: 0` reset on `.MuiInputBase-input`.
  searchInputEnd: {
    '& .MuiInputBase-root .MuiInputBase-input': {
      paddingLeft: '14px',
      paddingRight: '0px',
    },
  },
};

type SearchBarProps = TextFieldProps & {
  // 'start' (default) keeps the legacy layout used across the product. 'end'
  // moves the search icon to the trailing side behind a divider — used by
  // surfaces that follow the newer Figma spec.
  iconPosition?: 'start' | 'end';
  /**
   * Review finding G. Applied to the actual `<input>` (via `inputProps`), not the
   * TextField's root: `{...props}`'s own `data-testid` would land on the wrapper
   * div, unusable for a test that needs the input itself (to type into, or to
   * disambiguate it from another input on screen). Same pattern as
   * HeaderIconButton's `data-testid`.
   *
   * Falls back to `inputProps['data-testid']` when absent, for callers that
   * already set it there directly -- otherwise this prop being unset always
   * overwrote theirs with `undefined`.
   */
  'data-testid'?: string;
};

export function SearchBar({
  iconPosition = 'start',
  'data-testid': dataTestId,
  ...props
}: SearchBarProps) {
  const handleClear = () =>
    props.onChange?.({ target: { value: '' } } as React.ChangeEvent<HTMLInputElement>);

  const clearAdornment = props.value ? (
    <IconButton
      onClick={handleClear}
      size='small'
    >
      <ClearRounded fontSize='inherit' />
    </IconButton>
  ) : null;

  const startAdornment =
    iconPosition === 'start' ? (
      <InputAdornment position='start'>
        <SearchIcon sx={customStyles.searchIconStart} />
      </InputAdornment>
    ) : undefined;

  const endAdornment =
    iconPosition === 'end' ? (
      <InputAdornment position='end'>
        <Box sx={customStyles.searchEndAdornmentWrap}>
          {clearAdornment}
          <Box sx={customStyles.searchEndDivider} />
          <SearchIcon sx={customStyles.searchIconEnd} />
        </Box>
      </InputAdornment>
    ) : clearAdornment ? (
      <InputAdornment position='end'>{clearAdornment}</InputAdornment>
    ) : undefined;

  const combinedSx = {
    ...customStyles.root,
    ...(iconPosition === 'end' ? (customStyles.searchInputEnd as object) : null),
    ...((props.sx as object) ?? null),
  } as SxProps<Theme>;

  return (
    <TextField
      {...props}
      autoComplete='off'
      sx={combinedSx}
      InputProps={{ startAdornment, endAdornment }}
      inputProps={{ ...props.inputProps, 'data-testid': dataTestId ?? props.inputProps?.['data-testid'] }}
    />
  );
}
